import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { BiggerThanSmallAndSmallerThanMediumText } from "../../common/styles/Text.styles";
import {
  iconGap,
  perelynLightGreen,
  perelynLightRed,
  perelynLightYellow,
  perelynMediumGreen,
  smallGap,
  tinyGap,
} from "../../styles/consts";

interface EmailKeyword {
  keyword: string;
  source: "required" | "nice_to_have" | "tag";
  in_email: boolean;
  in_cv: boolean;
  email_count: number;
  cv_count: number;
}

interface EmailKeywordAnalysisData {
  keywords: EmailKeyword[];
  summary: {
    total: number;
    in_email: number;
    missing_from_email: number;
  };
}

interface EmailKeywordAnalysisProps {
  emailKeywordAnalysis: EmailKeywordAnalysisData;
  onRefresh: () => Promise<void>;
  isRefreshing: boolean;
}

const SOURCE_LABELS: Record<string, string> = {
  required: "Required Skills",
  nice_to_have: "Nice-to-Have Skills",
  tag: "Tags",
};

const SOURCE_ORDER = ["required", "nice_to_have", "tag"];

function getChipConfig(kw: EmailKeyword) {
  if (kw.in_email && kw.in_cv) {
    return {
      bgColor: perelynLightGreen,
      textColor: "#1B5E20",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "#1B5E20" }} />,
      tooltip: "In email and in CV",
    };
  }
  if (kw.in_email && !kw.in_cv) {
    return {
      bgColor: perelynLightYellow,
      textColor: "#7A4F01",
      icon: <WarningAmberIcon sx={{ fontSize: 14, color: "#E65100" }} />,
      tooltip: "In email but NOT in CV — may be inaccurate",
    };
  }
  if (!kw.in_email && kw.in_cv) {
    return {
      bgColor: perelynLightRed,
      textColor: "#B71C1C",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "#1B5E20" }} />,
      tooltip: "Not in email but IS in CV — missed opportunity",
    };
  }
  // !in_email && !in_cv
  return {
    bgColor: perelynLightRed,
    textColor: "#B71C1C",
    icon: <RemoveIcon sx={{ fontSize: 14, color: "#B71C1C" }} />,
    tooltip: "Not in email, not in CV",
  };
}

export default function EmailKeywordAnalysis({
  emailKeywordAnalysis,
  onRefresh,
  isRefreshing,
}: EmailKeywordAnalysisProps) {
  if (
    !emailKeywordAnalysis ||
    !emailKeywordAnalysis.keywords ||
    emailKeywordAnalysis.keywords.length === 0
  ) {
    return null;
  }

  const { keywords, summary } = emailKeywordAnalysis;
  const coveragePercent =
    summary.total > 0 ? (summary.in_email / summary.total) * 100 : 0;

  const grouped: Record<string, EmailKeyword[]> = {};
  for (const kw of keywords) {
    if (!grouped[kw.source]) {
      grouped[kw.source] = [];
    }
    grouped[kw.source].push(kw);
  }

  return (
    <Stack sx={{ padding: smallGap, paddingTop: 0, gap: smallGap }}>
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <BiggerThanSmallAndSmallerThanMediumText>
          Email Keyword Coverage
        </BiggerThanSmallAndSmallerThanMediumText>
        <Button
          size="small"
          variant="outlined"
          onClick={onRefresh}
          disabled={isRefreshing}
          startIcon={
            isRefreshing ? (
              <CircularProgress size={14} />
            ) : (
              <RefreshIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          Refresh
        </Button>
      </Stack>

      <Stack sx={{ gap: tinyGap }}>
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.875rem" }}>
            {summary.in_email}/{summary.total} keywords in email
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
            {Math.round(coveragePercent)}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={coveragePercent}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: perelynLightRed,
            "& .MuiLinearProgress-bar": {
              backgroundColor: perelynMediumGreen,
              borderRadius: 3,
            },
          }}
        />
      </Stack>

      {SOURCE_ORDER.map((source) => {
        const group = grouped[source];
        if (!group || group.length === 0) return null;

        return (
          <Stack key={source} sx={{ gap: tinyGap }}>
            <Typography
              sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#666" }}
            >
              {SOURCE_LABELS[source] || source}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: iconGap }}>
              {group.map((kw) => {
                const config = getChipConfig(kw);
                const label = kw.in_email
                  ? `${kw.keyword} (${kw.email_count})`
                  : kw.keyword;

                return (
                  <Tooltip
                    key={`${source}-${kw.keyword}`}
                    title={config.tooltip}
                    arrow
                  >
                    <Chip
                      label={label}
                      size="small"
                      icon={config.icon}
                      sx={{
                        backgroundColor: config.bgColor,
                        color: config.textColor,
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        border: "none",
                        "& .MuiChip-label": {
                          padding: "0 8px",
                        },
                        "& .MuiChip-icon": {
                          marginLeft: "4px",
                          marginRight: "-4px",
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
}
